import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

import {
  UpdateQueueETagAction,
  actionAndETagUpdate$,
} from '®shared/container-structure/edit/queue';
import { EventsService } from '®shared/events/events.service';
import { ListBuildingListAction } from '®shared/events/list-building-action';
import { createListBuildingEventForList } from '®shared/events/list-building-events';
import { ReadingListService } from '®shared/reading-list/models';
import { getETagFromError } from '®shared/tarl-v3-jsonapi';

import {
  ListDetailsEditorActionTypes,
  ListDetailsEditorSaveAction,
  ListDetailsEditorSaveFailedAction,
  ListDetailsEditorSaveSucceededAction,
} from './list-details-editor.actions';

@Injectable()
export class ListDetailsEditorEffects {
  saveListDetails$: Observable<
    | ListDetailsEditorSaveSucceededAction
    | ListDetailsEditorSaveFailedAction
    | UpdateQueueETagAction
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(ListDetailsEditorActionTypes.LIST_DETAILS_EDITOR_SAVE),
      switchMap((action: ListDetailsEditorSaveAction) => {
        const { listId, listDetails } = action.payload;
        return this.readingListService
          .updateReadingListDetails(listId, listDetails)
          .pipe(
            switchMap(updatedListDetails =>
              actionAndETagUpdate$(
                new ListDetailsEditorSaveSucceededAction(action.payload),
                updatedListDetails.list_etag,
              ),
            ),
            catchError(error =>
              actionAndETagUpdate$(
                new ListDetailsEditorSaveFailedAction({
                  ...action.payload,
                  error,
                }),
                getETagFromError(error),
              ),
            ),
          );
      }),
    ),
  );

  /**
   * Submit an Echo event for use in the List Building MI Dashboard
   *
   * This effect does not live in ListBuildingEventsEffects since that class is only included in the
   * ContainerStructureEditModule and so will only trigger for actions originating from List Edit.
   * Since the List Details Editor is present in List View as well as List Edit, we include it here instead.
   * We should move this effect to the ListBuildingEventsEffects class once we have removed the ability
   * to edit list details from the List View, when all customers are using New List Edit
   */

  recordUpdateListDetailsEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ListDetailsEditorActionTypes.LIST_DETAILS_EDITOR_SAVE_SUCCEEDED),
        tap((action: ListDetailsEditorSaveSucceededAction) => {
          const { listId, listDetails } = action.payload;
          const { license, timePeriod, listAccess } = listDetails;
          const license_id = license ? license.id : null;
          const period_id = timePeriod ? timePeriod.id : null;
          this.eventsService.recordEvent(
            createListBuildingEventForList(
              listId,
              ListBuildingListAction.LIST_UPDATE_DETAILS,
              { license_id, period_id, list_access: listAccess },
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private eventsService: EventsService,
    private readingListService: ReadingListService,
  ) {}
}
